import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Toaster, toast } from "sonner";
import {
  Package as PackageIcon,
  MagnifyingGlass,
  CheckCircle,
  Truck,
  ArrowLeft,
  Phone,
  EnvelopeSimple,
  Buildings,
  MapPin,
  MapPinLine,
  Path,
  Warning,
  CaretRight,
  Note,
} from "@phosphor-icons/react";

import { PUBLIC_BACKEND as BACKEND } from "@/lib/api";

const STATUS_LABEL = {
  pending: { fg: "text-amber-700", bg: "bg-amber-50", bdr: "border-amber-200", label: "Pending pickup" },
  in_transit: { fg: "text-blue-700", bg: "bg-blue-50", bdr: "border-blue-200", label: "In transit" },
  ready_for_pickup: { fg: "text-purple-700", bg: "bg-purple-50", bdr: "border-purple-200", label: "Ready for pickup" },
  awaiting_redelivery: { fg: "text-orange-700", bg: "bg-orange-50", bdr: "border-orange-200", label: "Awaiting redelivery" },
  return_to_sender: { fg: "text-red-700", bg: "bg-red-50", bdr: "border-red-200", label: "Returning to sender" },
  delivered: { fg: "text-emerald-700", bg: "bg-emerald-50", bdr: "border-emerald-200", label: "Delivered" },
  cancelled: { fg: "text-zinc-600", bg: "bg-zinc-50", bdr: "border-zinc-200", label: "Cancelled" },
};

export default function PublicTrack() {
  const { trackingNumber } = useParams();
  const navigate = useNavigate();
  const [input, setInput] = useState(trackingNumber || "");
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const lookup = async (tn) => {
    const code = (tn || "").trim().toUpperCase();
    if (!code) {
      toast.error("Enter a tracking number");
      return;
    }
    setLoading(true);
    setNotFound(false);
    setPkg(null);
    try {
      const { data } = await axios.get(`${BACKEND}/api/p/track/${encodeURIComponent(code)}`);
      setPkg(data);
    } catch (e) {
      if (e.response?.status === 404) {
        setNotFound(true);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (trackingNumber) {
      setInput(trackingNumber);
      lookup(trackingNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackingNumber]);

  const submit = (e) => {
    e.preventDefault();
    const code = (input || "").trim().toUpperCase();
    if (!code) return;
    navigate(`/track/${encodeURIComponent(code)}`);
    if (code === (trackingNumber || "").toUpperCase()) {
      // Same code — just re-fetch
      lookup(code);
    }
  };

  const status = pkg ? STATUS_LABEL[pkg.status] || { fg: "text-zinc-700", bg: "bg-zinc-50", bdr: "border-zinc-200", label: pkg.status } : null;

  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid="public-track-page">
      <Toaster position="top-right" />

      {/* Header */}
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-[900px] mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="track-back-home">
            <img src="/brand/kanal-logo.svg" alt="Kanal" className="h-9 w-9 bg-zinc-950 rounded p-1" />
            <div className="leading-tight">
              <div className="font-semibold tracking-tight text-lg" style={{ fontFamily: "Work Sans" }}>Kanal</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Track your package</div>
            </div>
          </Link>
          <Link to="/" className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1.5" data-testid="track-home-link">
            <ArrowLeft size={12} weight="bold" /> Home
          </Link>
        </div>
      </header>

      {/* Search */}
      <section className="max-w-[900px] mx-auto px-6 pt-10 pb-6">
        <div className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#002BF6] bg-[#002BF6]/8 border border-[#002BF6]/15 px-3 py-1.5 rounded-full mb-5">
          <PackageIcon size={11} weight="bold" /> Public tracking · No login required
        </div>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-2" style={{ fontFamily: "Work Sans" }}>
          Where is my package?
        </h1>
        <p className="text-sm text-zinc-600 mb-6">
          Enter the tracking number you received from your courier. It usually starts with <code className="font-mono text-xs bg-zinc-100 px-1.5 py-0.5 rounded">KN-</code>.
        </p>
        <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2" data-testid="track-search-form">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value.toUpperCase())}
            placeholder="KN-12345"
            className="font-mono h-14 sm:h-12 text-base flex-1"
            data-testid="track-input"
          />
          <Button
            type="submit"
            disabled={loading}
            className="bg-[#002BF6] hover:bg-[#0021C7] text-white h-14 sm:h-12 px-6"
            data-testid="track-submit-btn"
          >
            <MagnifyingGlass size={14} weight="bold" className="mr-1.5" />
            {loading ? "Looking up…" : "Track package"}
          </Button>
        </form>
      </section>

      {/* Result */}
      <section className="max-w-[900px] mx-auto px-6 pb-16">
        {notFound && (
          <div className="border border-amber-200 bg-amber-50 rounded-lg p-6 mt-4" data-testid="track-not-found">
            <div className="flex items-start gap-3">
              <PackageIcon size={24} weight="bold" className="text-amber-700 shrink-0" />
              <div>
                <div className="font-semibold text-amber-900">We couldn't find that tracking number.</div>
                <p className="text-sm text-amber-800 mt-1">
                  Double-check the format (e.g. <code className="font-mono bg-amber-100 px-1 rounded">KN-12345</code>) and try again.
                  If you still don't see it, contact your courier company directly.
                </p>
              </div>
            </div>
          </div>
        )}

        {pkg && status && (
          <div className="mt-4 border border-zinc-200 rounded-lg overflow-hidden shadow-sm" data-testid="track-result">
            {/* Status header */}
            <div className={`${status.bg} ${status.bdr} border-b px-6 py-5`}>
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-1">Tracking #</div>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <code className="font-mono text-2xl font-semibold text-zinc-900" data-testid="track-result-number">
                  {pkg.tracking_number}
                </code>
                <span className={`${status.fg} ${status.bg} ${status.bdr} border px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5`} data-testid="track-result-status">
                  {pkg.status === "delivered" ? <CheckCircle size={13} weight="fill" /> : <Truck size={13} weight="bold" />}
                  {status.label}
                </span>
              </div>
            </div>

            {/* Details */}
            <div className="bg-white px-6 py-5 grid sm:grid-cols-2 gap-5">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-1">From</div>
                <div className="text-sm font-medium text-zinc-900">{pkg.sender_name || "—"}</div>
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-1">To</div>
                <div className="text-sm font-medium text-zinc-900">{pkg.recipient_name || "—"}</div>
                {pkg.recipient_address && (
                  <div className="text-xs text-zinc-500 mt-1 flex items-start gap-1">
                    <MapPin size={11} weight="bold" className="mt-0.5 shrink-0" />
                    {pkg.recipient_address}
                  </div>
                )}
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-1">Weight</div>
                <div className="text-sm font-medium text-zinc-900">
                  {pkg.weight_kg ? `${Number(pkg.weight_kg).toFixed(2)} kg / ${(Number(pkg.weight_kg) * 2.20462).toFixed(2)} lb` : "—"}
                </div>
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-1">Method</div>
                <div className="text-sm font-medium text-zinc-900 capitalize">{pkg.shipment_method || "—"}</div>
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-1">Shipped</div>
                <div className="text-sm font-medium text-zinc-900">
                  {pkg.created_at ? new Date(pkg.created_at).toLocaleDateString() : "—"}
                </div>
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-1">Last updated</div>
                <div className="text-sm font-medium text-zinc-900">
                  {pkg.updated_at ? new Date(pkg.updated_at).toLocaleString() : "—"}
                </div>
              </div>
            </div>

            {/* Hub Trail — chronological scan events from the courier's hub network. */}
            <HubTrail events={pkg.events || []} status={pkg.status} createdAt={pkg.created_at} updatedAt={pkg.updated_at} />

            {/* Courier company card */}
            {pkg.company && (
              <div className="bg-zinc-50 border-t border-zinc-200 px-6 py-5" data-testid="track-courier-info">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-md bg-white border border-zinc-200 flex items-center justify-center">
                    <Buildings size={18} weight="bold" className="text-zinc-500" />
                  </div>
                  <div className="flex-1">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-0.5">Shipped by</div>
                    <div className="text-sm font-semibold text-zinc-900">{pkg.company.name || "Your courier"}</div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-600 mt-1">
                      {pkg.company.contact_email && (
                        <a href={`mailto:${pkg.company.contact_email}`} className="inline-flex items-center gap-1 hover:text-[#002BF6]">
                          <EnvelopeSimple size={11} weight="bold" /> {pkg.company.contact_email}
                        </a>
                      )}
                      {pkg.company.phone && (
                        <a href={`tel:${pkg.company.phone}`} className="inline-flex items-center gap-1 hover:text-[#002BF6]">
                          <Phone size={11} weight="bold" /> {pkg.company.phone}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {!pkg && !notFound && !loading && !trackingNumber && (
          <div className="border border-dashed border-zinc-200 rounded-lg p-8 mt-4 text-center text-sm text-zinc-500" data-testid="track-empty">
            Enter a tracking number above to see live delivery status.
          </div>
        )}
      </section>

      <footer className="border-t border-zinc-200 mt-auto">
        <div className="max-w-[900px] mx-auto px-6 py-6 text-xs text-zinc-500 flex flex-wrap items-center justify-between gap-2">
          <div>© {new Date().getFullYear()} Kanal · The Courier & Logistics Operating System</div>
          <Link to="/" className="hover:text-zinc-900">Back to home</Link>
        </div>
      </footer>
    </div>
  );
}

const EVENT_PRESET = {
  scan: { Icon: Path, color: "text-blue-600", bg: "bg-blue-50 border-blue-200", label: "Scan" },
  checkpoint: { Icon: MapPinLine, color: "text-emerald-600", bg: "bg-emerald-50 border-emerald-200", label: "Checkpoint" },
  out_for_delivery: { Icon: Truck, color: "text-amber-600", bg: "bg-amber-50 border-amber-200", label: "Out for delivery" },
  exception: { Icon: Warning, color: "text-red-600", bg: "bg-red-50 border-red-200", label: "Exception" },
  note: { Icon: Note, color: "text-zinc-600", bg: "bg-zinc-50 border-zinc-200", label: "Note" },
};

function HubTrail({ events, status, createdAt, updatedAt }) {
  // Build the timeline: always include "Shipped" as the first item; append scans;
  // append a "Delivered" cap when status === "delivered". Customer-facing, no PII.
  const items = [];
  if (createdAt) {
    items.push({
      id: "__shipped",
      Icon: PackageIcon,
      color: "text-zinc-600",
      bg: "bg-zinc-100 border-zinc-200",
      label: "Shipped",
      branch_name: null,
      notes: "Package created in courier system",
      created_at: createdAt,
    });
  }
  (events || []).forEach((e) => {
    const preset = EVENT_PRESET[e.event_type] || EVENT_PRESET.scan;
    items.push({
      id: e.id,
      Icon: preset.Icon,
      color: preset.color,
      bg: preset.bg,
      label: preset.label,
      branch_name: e.branch_name,
      notes: e.notes,
      created_at: e.created_at,
    });
  });
  if (status === "delivered") {
    items.push({
      id: "__delivered",
      Icon: CheckCircle,
      color: "text-emerald-600",
      bg: "bg-emerald-50 border-emerald-200",
      label: "Delivered",
      branch_name: null,
      notes: "Package successfully delivered",
      created_at: updatedAt,
    });
  }

  return (
    <div className="bg-white border-t border-zinc-200 px-6 py-5" data-testid="track-hub-trail">
      <div className="flex items-center gap-2 mb-4">
        <Path size={14} weight="bold" className="text-zinc-700" />
        <h2 className="text-sm font-semibold tracking-tight text-zinc-900" style={{ fontFamily: "Work Sans" }}>
          Hub trail
        </h2>
        <span className="text-[11px] text-zinc-500">{items.length} {items.length === 1 ? "event" : "events"}</span>
      </div>

      {items.length === 0 ? (
        <div className="text-xs text-zinc-500 italic" data-testid="track-hub-trail-empty">
          No scan events yet. Your courier will start scanning the package at each hub along the route.
        </div>
      ) : (
        <ol className="relative space-y-5 pl-6 before:absolute before:left-[10px] before:top-1 before:bottom-1 before:w-px before:bg-zinc-200">
          {items.map((it, idx) => {
            const isLast = idx === items.length - 1;
            return (
              <li key={it.id} className="relative" data-testid={`track-event-${idx}`}>
                <div className={`absolute -left-6 top-0.5 w-5 h-5 rounded-full border ${it.bg} flex items-center justify-center`}>
                  <it.Icon size={11} weight="bold" className={it.color} />
                </div>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0">
                    <div className={`text-sm font-semibold ${isLast ? "text-zinc-900" : "text-zinc-700"}`}>
                      {it.label}
                      {it.branch_name && (
                        <span className="text-zinc-500 font-normal"> · <CaretRight size={10} weight="bold" className="inline -mt-0.5" /> {it.branch_name}</span>
                      )}
                    </div>
                    {it.notes && <div className="text-xs text-zinc-500 mt-0.5">{it.notes}</div>}
                  </div>
                  <div className="text-[11px] text-zinc-500 font-mono whitespace-nowrap">
                    {it.created_at ? new Date(it.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
